import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery } from 'react-query';
import api, { getImageUrl } from '../../services/api';
import Logo from '../../components/Logo';
import CheckoutModal from '../../components/CheckoutModal';
import { 
  ShoppingCart, 
  Search, 
  Filter,
  Plus,
  Minus,
  X,
  MapPin,
  Clock,
  Star,
  ChevronRight,
  Heart,
  Phone,
  Mail,
  Instagram,
  Facebook,
  MessageCircle
} from 'lucide-react';
import toast from 'react-hot-toast';
import './Cardapio.css';

const CATEGORIA_CARNES_DO_DIA = 'CARNES DO DIA';
const CATEGORIA_CARNE_EXTRA = 'CARNES DO DIA - EXTRA';
const NOME_CARNE_EXTRA = 'Carne extra';
const PREFIXO_GUARNICAO = 'GUARNIÇÃO: ';
const PREFIXO_CARNE = 'CARNE: ';

const normalizarTexto = (v) => String(v || '').trim().toLowerCase();

const isProdutoMarmitex = (produto) => {
  const categoria = normalizarTexto(produto?.categoria);
  if (categoria === 'marmitex') return true;
  const nome = normalizarTexto(produto?.nome);
  return nome.includes('marmitex');
};

const obterLimiteCarnes = (produto, variacao) => {
  const base = normalizarTexto(variacao?.nome || produto?.nome);
  if (base.includes('pequena')) return 1;
  if (base.includes('media') || base.includes('média')) return 2;
  if (base.includes('grande')) return 3;
  return 1;
};

const contarCarnesSelecionadas = (adicionais) => {
  const arr = Array.isArray(adicionais) ? adicionais : [];
  return arr
    .filter((a) => normalizarTexto(a?.nome).startsWith(normalizarTexto(PREFIXO_CARNE)))
    .reduce((acc, a) => acc + Math.min(20, Math.max(1, Number(a?.quantidade || 1))), 0);
};

const getAdicionalKey = (a) => String(a?._id || a?.nome || '');

const Cardapio = () => {
  const { estabelecimentoId } = useParams();
  const navigate = useNavigate();
  const [categoriaAtiva, setCategoriaAtiva] = useState('todas');
  const [busca, setBusca] = useState('');
  const [carrinho, setCarrinho] = useState([]);
  const [modalProduto, setModalProduto] = useState(null);
  const [adicionaisSelecionados, setAdicionaisSelecionados] = useState([]);
  const [variacaoSelecionada, setVariacaoSelecionada] = useState(null);
  const [adicionaisAberto, setAdicionaisAberto] = useState(false);
  const [quantidade, setQuantidade] = useState(1);
  const [observacoes, setObservacoes] = useState('');
  const [marmitexSemCarne, setMarmitexSemCarne] = useState(false);
  const [marmitexPermitirExtra, setMarmitexPermitirExtra] = useState(false);
  const [checkoutOpen, setCheckoutOpen] = useState(false);
  const [carrinhoModalOpen, setCarrinhoModalOpen] = useState(false);
  const [podeFazerPedido, setPodeFazerPedido] = useState(true);

  // Adicionar classe ao body quando modal estiver aberto
  useEffect(() => {
    if (modalProduto) {
      document.body.classList.add('modal-aberto');
    } else {
      document.body.classList.remove('modal-aberto');
    }
    return () => {
      document.body.classList.remove('modal-aberto');
    };
  }, [modalProduto]);


  // Buscar estabelecimento padrão (Cantinho do Sabor) - APENAS UMA VEZ
  const { data: estabelecimento, isLoading: loadingEstabelecimento, error: errorEstabelecimento, isError: isErrorEstabelecimento } = useQuery(
    ['estabelecimento-padrao'], // Chave fixa, sem parâmetros
    async () => {
      let estabelecimentoData;
      try {
        const response = await api.get('/api/estabelecimentos/padrao');
        estabelecimentoData = response.data.data.estabelecimento;
      } catch (e) {
        const response = await api.get('/api/estabelecimentos?ativo=true');
        const lista = response.data.data.estabelecimentos || [];
        estabelecimentoData = lista[0] || null;
      }

      if (!estabelecimentoData?._id) {
        throw new Error('Estabelecimento padrão não encontrado');
      }
      
      // Verificar se aceita pedidos
      const aceitaPedidos = estabelecimentoData?.configuracoes?.aceitaPedidos !== false;
      setPodeFazerPedido(aceitaPedidos);
      
      // Log para debug
      console.log('✅ Estabelecimento carregado:', {
        id: estabelecimentoData._id,
        nome: estabelecimentoData.nome,
        temConfig: !!estabelecimentoData.configuracoes,
        temTaxas: !!estabelecimentoData.configuracoes?.taxasPorBairro,
        qtdTaxas: estabelecimentoData.configuracoes?.taxasPorBairro?.length || 0,
        aceitaPedidos
      });
      
      return estabelecimentoData;
    },
    {
      retry: 2,
      retryDelay: 1000,
      refetchOnWindowFocus: false, // Não buscar novamente ao focar na janela
      refetchOnMount: false, // Não buscar novamente ao montar
      refetchOnReconnect: false, // Não buscar novamente ao reconectar
      refetchInterval: 10000, // Verificar status a cada 10 segundos
      staleTime: Infinity, // Dados nunca ficam "stale" - são válidos para sempre
      cacheTime: Infinity, // Cachear para sempre (não limpar)
      onError: (error) => {
        console.error('❌ Erro ao buscar estabelecimento:', error);
        if (error.code === 'ECONNABORTED' || error.message?.includes('timeout')) {
          toast.error('⏱️ Tempo de resposta excedido. Verifique sua conexão.');
        } else if (error.response?.status === 404) {
          toast.error('❌ Estabelecimento não encontrado.');
        } else {
          toast.error('❌ Erro ao carregar estabelecimento. Verifique se o servidor está rodando.');
        }
      }
    }
  );

  const { data: produtos, isLoading: loadingProdutos, error: errorProdutos, isError: isErrorProdutos } = useQuery(
    ['produtos', estabelecimento?._id],
    async () => {
      if (!estabelecimento?._id) return [];
      const response = await api.get(`/api/produtos?estabelecimento=${estabelecimento._id}&disponivel=true`);
      const produtosList = response.data.data.produtos || [];
      
      // ✅ Debug: Verificar URLs das imagens
      console.log('📦 Produtos carregados:', produtosList.length);
      produtosList.forEach(produto => {
        if (produto.imagens && produto.imagens.length > 0) {
          const urlOriginal = produto.imagens[0].url;
          const urlFinal = getImageUrl(urlOriginal);
          console.log(`🖼️ ${produto.nome}:`, {
            urlOriginal,
            urlFinal,
            acessivel: urlFinal ? '✅' : '❌'
          });
        } else {
          console.log(`⚠️ ${produto.nome}: SEM IMAGEM`);
        }
      });
      
      return produtosList;
    },
    {
      enabled: !!estabelecimento?._id,
      retry: 1,
      retryDelay: 2000,
      refetchOnWindowFocus: false,
      onError: (error) => {
        console.error('❌ Erro ao buscar produtos:', error);
        if (error.code === 'ECONNABORTED' || error.message?.includes('timeout')) {
          toast.error('⏱️ Tempo de resposta excedido ao buscar produtos.');
        } else {
          toast.error('❌ Erro ao carregar produtos. Tente novamente.');
        }
      }
    }
  );

  // Buscar adicionais globais
  const { data: adicionaisGlobais } = useQuery(
    ['adicionais-globais', estabelecimento?._id],
    async () => {
      if (!estabelecimento?._id) return [];
      const response = await api.get(`/api/adicionais?estabelecimento=${estabelecimento._id}&disponivel=true`);
      return response.data.data.adicionais || [];
    },
    {
      enabled: !!estabelecimento?._id,
      staleTime: 5 * 60 * 1000,
      cacheTime: 10 * 60 * 1000,
    }
  );

  const isLoading = loadingEstabelecimento || loadingProdutos;

  // Tratamento de erro - mostrar tela de erro se houver falha crítica
  if (isErrorEstabelecimento && !estabelecimento) {
    return (
      <div className="cardapio-page">
        <div style={{ 
          display: 'flex', 
          flexDirection: 'column', 
          alignItems: 'center', 
          justifyContent: 'center', 
          minHeight: '100vh',
          padding: '2rem',
          textAlign: 'center',
          backgroundColor: '#f8f9fa'
        }}>
          <div style={{ fontSize: '4rem', marginBottom: '1rem' }}>⚠️</div>
          <h2 style={{ color: '#e74c3c', marginBottom: '1rem', fontSize: '1.5rem' }}>Erro ao carregar estabelecimento</h2>
          <p style={{ marginBottom: '2rem', color: '#666', maxWidth: '500px' }}>
            Não foi possível conectar ao servidor. Verifique se o backend está rodando em <strong>http://localhost:5000</strong>
          </p>
          <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap', justifyContent: 'center' }}>
            <button 
              onClick={() => window.location.reload()} 
              style={{
                padding: '0.75rem 2rem',
                backgroundColor: '#3498db',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                cursor: 'pointer',
                fontSize: '1rem',
                fontWeight: 'bold'
              }}
            >
              🔄 Tentar Novamente
            </button>
            <button 
              onClick={() => window.open('http://localhost:5000/api/estabelecimentos/padrao', '_blank')}
              style={{
                padding: '0.75rem 2rem',
                backgroundColor: '#95a5a6',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                cursor: 'pointer',
                fontSize: '1rem'
              }}
            >
              🔍 Testar API
            </button>
          </div>
          {errorEstabelecimento?.message && (
            <p style={{ marginTop: '2rem', fontSize: '0.85rem', color: '#999', fontFamily: 'monospace' }}>
              {errorEstabelecimento.message}
            </p>
          )}
        </div>
      </div>
    );
  }

  // Categorias únicas
  const categorias = ['todas', ...new Set(produtos?.map(p => p.categoria).filter(Boolean) || [])];

  // Filtrar produtos
  const produtosFiltrados = produtos?.filter(produto => {
    const matchCategoria = categoriaAtiva === 'todas' || produto.categoria === categoriaAtiva;
    const matchBusca = !busca || produto.nome.toLowerCase().includes(busca.toLowerCase()) || 
                      produto.descricao?.toLowerCase().includes(busca.toLowerCase());
    return matchCategoria && matchBusca && produto.disponivel;
  }) || [];

  // Calcular total do carrinho
  const totalCarrinho = carrinho.reduce((acc, item) => {
    const precoProduto = item.precoPromocional || item.preco;
    const precoAdicionais = item.adicionais?.reduce((sum, add) => {
      const qtd = Math.min(20, Math.max(1, Number(add?.quantidade || 1)));
      return sum + ((add.preco || 0) * qtd);
    }, 0) || 0;
    return acc + ((precoProduto + precoAdicionais) * item.quantidade);
  }, 0);

  // Calcular preço total em tempo real (para exibir no botão)
  const calcularPrecoTotal = () => {
    if (!modalProduto) return 0;
    
    let precoBase = modalProduto.precoPromocional || modalProduto.preco || 0;
    
    // Se tem variação, usar preço da variação
    if (modalProduto.temVariacoes && variacaoSelecionada) {
      precoBase = variacaoSelecionada.preco;
    }
    
    const isMarmitex = isProdutoMarmitex(modalProduto);
    const carneExtraCfg = (Array.isArray(adicionaisGlobais) ? adicionaisGlobais : []).find((a) => {
      return normalizarTexto(a?.categoria) === normalizarTexto(CATEGORIA_CARNE_EXTRA) && normalizarTexto(a?.nome) === normalizarTexto(NOME_CARNE_EXTRA);
    });

    const adicionaisBase = Array.isArray(adicionaisSelecionados) ? adicionaisSelecionados : [];
    let adicionaisFinal = adicionaisBase;

    if (isMarmitex) {
      const limite = obterLimiteCarnes(modalProduto, variacaoSelecionada);
      const totalCarnes = marmitexSemCarne ? 0 : contarCarnesSelecionadas(adicionaisBase);
      const excedente = Math.max(0, totalCarnes - limite);

      if (marmitexSemCarne) {
        adicionaisFinal = adicionaisFinal.filter((a) => !normalizarTexto(a?.nome).startsWith(normalizarTexto(PREFIXO_CARNE)));
      }

      if (excedente > 0 && marmitexPermitirExtra && carneExtraCfg && carneExtraCfg.disponivel !== false) {
        adicionaisFinal = [
          ...adicionaisFinal,
          { nome: NOME_CARNE_EXTRA, preco: Number(carneExtraCfg.preco) || 0, quantidade: excedente }
        ];
      }
    }

    const precoAdicionais = adicionaisFinal.reduce((sum, add) => {
      const qtd = Math.min(20, Math.max(1, Number(add?.quantidade || 1)));
      return sum + ((add.preco || 0) * qtd);
    }, 0);
    
    return (precoBase + precoAdicionais) * quantidade;
  };

  const adicionarAoCarrinho = () => {
    if (!modalProduto) return;

    // Verificar se pode fazer pedido
    if (!podeFazerPedido) {
      toast.error('😔 O estabelecimento não está aceitando pedidos no momento. Tente novamente mais tarde.', {
        duration: 5000,
        icon: '🚫'
      });
      return;
    }

    // Validar estoque antes de adicionar
    const estoqueDisponivel = modalProduto.estoque?.quantidade || 999999;
    
    // Verificar quantidade no carrinho atual do mesmo produto
    const quantidadeNoCarrinho = carrinho
      .filter(item => item._id === modalProduto._id)
      .reduce((sum, item) => sum + (item.quantidade || 0), 0);
    
    const quantidadeTotal = quantidadeNoCarrinho + quantidade;
    
    if (quantidadeTotal > estoqueDisponivel) {
      const disponivel = Math.max(0, estoqueDisponivel - quantidadeNoCarrinho);
      if (disponivel === 0) {
        toast.error(`❌ ${modalProduto.nome} está fora de estoque!`);
        return;
      }
      toast.error(`❌ Estoque insuficiente! Disponível: ${disponivel} unidade(s)`);
      setQuantidade(disponivel);
      return;
    }

    // Se o produto tem variações, usar a variação selecionada
    let precoFinal = modalProduto.precoPromocional || modalProduto.preco;
    let nomeFinal = modalProduto.nome;
    
    if (modalProduto.temVariacoes && modalProduto.variacoes && modalProduto.variacoes.length > 0) {
      if (!variacaoSelecionada) {
        toast.error('Selecione um tamanho antes de adicionar ao carrinho');
        return;
      }
      precoFinal = variacaoSelecionada.preco;
      nomeFinal = `${modalProduto.nome} - ${variacaoSelecionada.nome}`;
    }

    const isMarmitex = isProdutoMarmitex(modalProduto);
    const carneExtraCfg = (Array.isArray(adicionaisGlobais) ? adicionaisGlobais : []).find((a) => {
      return normalizarTexto(a?.categoria) === normalizarTexto(CATEGORIA_CARNE_EXTRA) && normalizarTexto(a?.nome) === normalizarTexto(NOME_CARNE_EXTRA);
    });

    const adicionaisBase = Array.isArray(adicionaisSelecionados) ? adicionaisSelecionados : [];
    let adicionaisFinal = adicionaisBase.filter((a) => normalizarTexto(a?.nome) !== normalizarTexto(NOME_CARNE_EXTRA));

    if (isMarmitex) {
      const limite = obterLimiteCarnes(modalProduto, variacaoSelecionada);
      const totalCarnes = marmitexSemCarne ? 0 : contarCarnesSelecionadas(adicionaisFinal);
      const excedente = Math.max(0, totalCarnes - limite);

      if (excedente > 0 && !marmitexPermitirExtra) {
        toast.error(`Limite de carnes: ${limite}. Marque a opção de carne extra para adicionar mais.`);
        return;
      }

      if (excedente > 0 && (!carneExtraCfg || carneExtraCfg.disponivel === false)) {
        toast.error('Carne extra não configurada/indisponível. Ative e defina o preço antes de adicionar carnes acima do limite.');
        return;
      }

      if (marmitexSemCarne) {
        adicionaisFinal = adicionaisFinal.filter((a) => !normalizarTexto(a?.nome).startsWith(normalizarTexto(PREFIXO_CARNE)));
      }

      if (excedente > 0 && carneExtraCfg && carneExtraCfg.disponivel !== false) {
        adicionaisFinal = [
          ...adicionaisFinal,
          { nome: NOME_CARNE_EXTRA, preco: Number(carneExtraCfg.preco) || 0, quantidade: excedente }
        ];
      }
    }

    const item = {
      ...modalProduto,
      nome: nomeFinal,
      quantidade,
      adicionais: adicionaisFinal,
      observacoes,
      precoOriginal: modalProduto.preco,
      preco: precoFinal,
      variacao: variacaoSelecionada ? {
        nome: variacaoSelecionada.nome,
        preco: variacaoSelecionada.preco
      } : null
    };

    setCarrinho([...carrinho, item]);
    toast.success(`${quantidade}x ${nomeFinal} adicionado ao carrinho!`);
    setModalProduto(null);
    setAdicionaisSelecionados([]);
    setVariacaoSelecionada(null);
    setQuantidade(1);
    setObservacoes('');
    setMarmitexSemCarne(false);
    setMarmitexPermitirExtra(false);
    
    // Garantir que carrinho fique minimizado após adicionar produto
    setTimeout(() => {
      const carrinhoDetalhes = document.querySelector('.carrinho-detalhes');
      if (carrinhoDetalhes) {
        carrinhoDetalhes.classList.remove('aberto');
      }
    }, 50);
  };

  const removerDoCarrinho = (index) => {
    const novoCarrinho = carrinho.filter((_, i) => i !== index);
    setCarrinho(novoCarrinho);
    toast.success('Item removido do carrinho');
  };

  const fecharModalProduto = () => {
    setModalProduto(null);
    setAdicionaisSelecionados([]);
    setVariacaoSelecionada(null);
    setQuantidade(1);
    setObservacoes('');
    setMarmitexSemCarne(false);
    setMarmitexPermitirExtra(false);
  };

  const atualizarQuantidade = (index, novaQuantidade) => {
    if (novaQuantidade < 1) return;
    const novoCarrinho = [...carrinho];
    novoCarrinho[index].quantidade = novaQuantidade;
    setCarrinho(novoCarrinho);
  };

  const handleAbrirCheckout = () => {
    if (!podeFazerPedido) {
      toast.error('😔 O estabelecimento não está aceitando pedidos no momento. Tente novamente mais tarde.', {
        duration: 5000,
        icon: '🚫'
      });
      return;
    }
    if (carrinho.length === 0) {
      toast.error('Adicione itens ao carrinho primeiro');
      return;
    }
    setCheckoutOpen(true);
  };

  const toggleAdicional = (adicional) => {
    const key = getAdicionalKey(adicional);
    const existe = adicionaisSelecionados.find(a => getAdicionalKey(a) === key);
    if (existe) {
      setAdicionaisSelecionados(adicionaisSelecionados.filter(a => getAdicionalKey(a) !== key));
    } else {
      setAdicionaisSelecionados([...adicionaisSelecionados, { ...adicional, quantidade: 1 }]);
    }
  };

  const atualizarQuantidadeAdicional = (adicionalId, novaQuantidade) => {
    const qtd = Math.min(20, Math.max(1, Number(novaQuantidade || 1)));
    setAdicionaisSelecionados(prev =>
      prev.map(a => (getAdicionalKey(a) === String(adicionalId) ? { ...a, quantidade: qtd } : a))
    );
  };

  if (isLoading) {
    return (
      <div className="cardapio-loading">
        <div className="loading-spinner"></div>
        <p>Carregando cardápio...</p>
        {isErrorEstabelecimento && (
          <p style={{ marginTop: '1rem', color: '#e74c3c', fontSize: '0.9rem' }}>
            Tentando reconectar...
          </p>
        )}
      </div>
    );
  }

  return (
    <div className="cardapio-page">
      {/* Banner de status do estabelecimento */}
      {!podeFazerPedido && (
        <div style={{
          background: 'linear-gradient(135deg, #dc3545 0%, #c82333 100%)',
          color: 'white',
          padding: '1.25rem',
          textAlign: 'center',
          marginBottom: '0',
          boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
          position: 'sticky',
          top: 0,
          zIndex: 1000
        }}>
          <div style={{ 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'center', 
            gap: '1rem',
            maxWidth: '1200px',
            margin: '0 auto'
          }}>
            <div style={{
              width: '20px',
              height: '20px',
              borderRadius: '50%',
              background: '#fff',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '12px'
            }}>🚫</div>
            <div>
              <strong style={{ fontSize: '1.1rem', display: 'block', marginBottom: '0.25rem' }}>
                Estabelecimento FECHADO para pedidos online
              </strong>
              <small style={{ opacity: 0.9 }}>
                No momento não estamos aceitando novos pedidos. Tente novamente mais tarde.
              </small>
            </div>
          </div>
        </div>
      )}

      {podeFazerPedido && (
        <div style={{
          background: 'linear-gradient(135deg, #28a745 0%, #218838 100%)',
          color: 'white',
          padding: '0.75rem',
          textAlign: 'center',
          marginBottom: '0',
          position: 'sticky',
          top: 0,
          zIndex: 1000,
          fontSize: '0.9rem'
        }}>
          <div style={{ 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'center', 
            gap: '0.5rem'
          }}>
            <div style={{
              width: '12px',
              height: '12px',
              borderRadius: '50%',
              background: '#fff',
              animation: 'pulse 2s infinite'
            }} />
            <span>✅ Estabelecimento aberto - Pedidos online ativos</span>
          </div>
        </div>
      )}

      {/* Header Fixo */}
      <header className="cardapio-header">
        <div className="header-content">
          <Logo size="small" />
          <div className="header-info">
            <h1>{estabelecimento?.nome || 'Cantinho do Sabor'}</h1>
            {estabelecimento && (
              <div className="header-details">
                <span><MapPin size={14} /> {estabelecimento.endereco?.rua || 'Rua Alberto Boreli, 37'}</span>
                <span><Clock size={14} /> {estabelecimento.configuracoes?.tempoMedioPreparo || 50} min</span>
              </div>
            )}
          </div>
          <div className="header-actions">
            <div className="search-box-header">
              <Search size={20} />
              <input
                type="text"
                placeholder="Buscar produto..."
                value={busca}
                onChange={(e) => setBusca(e.target.value)}
              />
            </div>
            <button
              className="btn-carrinho"
              onClick={() => {
                if (carrinho.length === 0) {
                  toast.error('Carrinho vazio');
                  return;
                }
                setCarrinhoModalOpen(true);
              }}
            >
              <ShoppingCart size={24} />
              {carrinho.length > 0 && <span className="badge-carrinho">{carrinho.length}</span>}
            </button>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="hero-section">
        <div className="hero-content">
          <h2>Cardápio Digital</h2>
          <p>Os melhores lanches com sabor único!</p>
          <div className="hero-stats">
            <div className="stat-item">
              <Star size={20} fill="#FFD23F" color="#FFD23F" />
              <span>{estabelecimento?.avaliacao?.media?.toFixed(1) || '4.8'}</span>
            </div>
            <div className="stat-item">
              <span>+{produtos?.length || 0} produtos</span>
            </div>
            <div className="stat-item">
              <span>Entrega rápida</span>
            </div>
          </div>
        </div>
      </section>

      {/* Filtros de Categoria */}
      <section className="categorias-section">
        <div className="categorias-container">
          <div className="categorias-scroll">
            {categorias.map(categoria => (
              <button
                key={categoria}
                className={`categoria-btn ${categoriaAtiva === categoria ? 'ativa' : ''}`}
                onClick={() => setCategoriaAtiva(categoria)}
              >
                {categoria === 'todas' ? 'Todos' : categoria}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Grid de Produtos */}
      <section className="produtos-section">
        <div className="container">
          {produtosFiltrados.length === 0 ? (
            <div className="empty-state">
              <p>Nenhum produto encontrado</p>
            </div>
          ) : (
            <div className="produtos-grid">
              {produtosFiltrados.map(produto => (
                <div key={produto._id} className="produto-card">
                  <div className="produto-imagem">
                    {produto.imagens && produto.imagens.length > 0 && produto.imagens[0]?.url ? (
                      <>
                        <img 
                          src={getImageUrl(produto.imagens[0].url)} 
                          alt={produto.nome}
                          onLoad={() => {
                            console.log('✅ Imagem carregada:', produto.nome, produto.imagens[0].url);
                          }}
                          onError={(e) => {
                            const urlOriginal = produto.imagens[0]?.url;
                            const urlFinal = getImageUrl(urlOriginal);
                            console.error('❌ Erro ao carregar imagem:', {
                              produto: produto.nome,
                              urlOriginal: urlOriginal,
                              urlFinal: urlFinal,
                              windowOrigin: typeof window !== 'undefined' ? window.location.origin : 'N/A',
                              imagens: produto.imagens,
                              erro: e.target?.src
                            });
                            console.error('🔍 Tentando acessar:', urlFinal);
                            console.error('🔍 URL completa seria:', `${window.location.origin}${urlOriginal}`);
                            try {
                              if (e && e.target) {
                                e.target.style.display = 'none';
                                const parent = e.target.parentElement;
                                if (parent) {
                                  const placeholder = parent.querySelector('.produto-placeholder');
                                  if (placeholder) {
                                    placeholder.style.display = 'flex';
                                  }
                                }
                              }
                            } catch (error) {
                              console.error('Erro ao tratar falha de imagem:', error);
                            }
                          }}
                        />
                        <div className="produto-placeholder" style={{ display: 'none' }}>
                          <span>{produto.nome.charAt(0)}</span>
                        </div>
                      </>
                    ) : (
                      <div className="produto-placeholder">
                        <span>{produto.nome.charAt(0)}</span>
                      </div>
                    )}
                    {produto.precoPromocional && (
                      <span className="badge-promocao">PROMOÇÃO</span>
                    )}
                    {produto.maisVendido && (
                      <span className="badge-vendido">MAIS VENDIDO</span>
                    )}
                  </div>
                  <div className="produto-info">
                    <h3>{produto.nome}</h3>
                    <p className="produto-descricao">{produto.descricao || 'Delicioso e saboroso'}</p>
                    <div className="produto-footer">
                      <div className="produto-preco">
                        {produto.precoPromocional ? (
                          <>
                            <span className="preco-antigo">R$ {produto.preco.toFixed(2)}</span>
                            <span className="preco-novo">R$ {produto.precoPromocional.toFixed(2)}</span>
                          </>
                        ) : (
                          <span className="preco-normal">R$ {produto.preco?.toFixed(2)}</span>
                        )}
                      </div>
                      <button
                        className="btn-adicionar"
                        onClick={() => {
                          setModalProduto(produto);
                          setVariacaoSelecionada(null); // Resetar variação ao abrir modal
                          setQuantidade(1);
                          setAdicionaisSelecionados([]);
                          setAdicionaisAberto(false); // Fechar accordion ao abrir modal
                          setObservacoes('');
                          setMarmitexSemCarne(false);
                          setMarmitexPermitirExtra(false);
                        }}
                      >
                        <Plus size={20} />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Modal do Produto */}
      {modalProduto && (
        <div className="modal-overlay" onClick={fecharModalProduto}>
          <div className="modal-produto" onClick={(e) => e.stopPropagation()}>
            <button className="modal-close" onClick={fecharModalProduto}>
              <X size={24} />
            </button>
            
            <div className="modal-imagem">
              {modalProduto.imagens && modalProduto.imagens.length > 0 && modalProduto.imagens[0].url ? (
                <>
                  <img 
                    src={getImageUrl(modalProduto.imagens[0].url)} 
                    alt={modalProduto.nome}
                    onError={(e) => {
                      e.target.style.display = 'none';
                      const placeholder = e.target.parentElement?.querySelector('.modal-placeholder');
                      if (placeholder) {
                        placeholder.style.display = 'flex';
                      }
                    }}
                  />
                  <div className="modal-placeholder" style={{ display: 'none' }}>
                    <span>{modalProduto.nome.charAt(0)}</span>
                  </div>
                </>
              ) : (
                <div className="modal-placeholder">
                  <span>{modalProduto.nome.charAt(0)}</span>
                </div>
              )}
            </div>

            <div className="modal-content-scroll">
              <div className="modal-content">
              <h2>{modalProduto.nome}</h2>
              <p className="modal-descricao">{modalProduto.descricao || 'Delicioso e saboroso'}</p>
              
              <div className="modal-preco">
                {(() => {
                  // Se tem variações, mostrar seleção de variações
                  if (modalProduto.temVariacoes && modalProduto.variacoes && modalProduto.variacoes.length > 0) {
                    return (
                      <div style={{ marginBottom: '1.5rem' }}>
                        <h3 style={{ marginBottom: '1rem', fontSize: '1.1rem', fontWeight: '600' }}>Selecione o Tamanho:</h3>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                          {modalProduto.variacoes
                            .filter(v => v.disponivel !== false)
                            .map((variacao, index) => (
                              <label
                                key={index}
                                style={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  padding: '1rem',
                                  border: variacaoSelecionada?.nome === variacao.nome ? '2px solid #FF6B35' : '2px solid #E5E7EB',
                                  borderRadius: '0.75rem',
                                  cursor: 'pointer',
                                  background: variacaoSelecionada?.nome === variacao.nome ? '#FFF5F0' : 'white',
                                  transition: 'all 0.2s'
                                }}
                              >
                                <input
                                  type="radio"
                                  name="variacao"
                                  value={variacao.nome}
                                  checked={variacaoSelecionada?.nome === variacao.nome}
                                  onChange={() => setVariacaoSelecionada(variacao)}
                                  style={{ marginRight: '1rem', width: '20px', height: '20px' }}
                                />
                                <div style={{ flex: 1 }}>
                                  <div style={{ fontWeight: '600', marginBottom: '0.25rem' }}>{variacao.nome}</div>
                                  <div style={{ fontSize: '1.25rem', fontWeight: '700', color: '#FF6B35' }}>
                                    R$ {variacao.preco.toFixed(2)}
                                  </div>
                                </div>
                              </label>
                            ))}
                        </div>
                      </div>
                    );
                  } else {
                    // Produto sem variações - mostrar preço normal
                    return modalProduto.precoPromocional ? (
                      <>
                        <span className="preco-antigo">R$ {modalProduto.preco.toFixed(2)}</span>
                        <span className="preco-novo">R$ {modalProduto.precoPromocional.toFixed(2)}</span>
                      </>
                    ) : (
                      <span className="preco-normal">R$ {modalProduto.preco?.toFixed(2)}</span>
                    );
                  }
                })()}
              </div>

              {isProdutoMarmitex(modalProduto) && (
                <>
                  {modalProduto.adicionais && modalProduto.adicionais.length > 0 && (
                    <div className="modal-adicionais">
                      <div className="adicionais-header">
                        <h3>Guarnições</h3>
                      </div>
                      <div className="adicionais-content">
                        <div className="adicionais-grid">
                          {modalProduto.adicionais
                            .filter((g) => g && g.disponivel !== false)
                            .map((guarnicao, idx) => {
                              const nomeMarcado = `${PREFIXO_GUARNICAO}${guarnicao.nome}`;
                              const checked = adicionaisSelecionados.some((a) => normalizarTexto(a?.nome) === normalizarTexto(nomeMarcado));

                              return (
                                <label key={`${guarnicao.nome}-${idx}`} className="adicional-item">
                                  <input
                                    type="checkbox"
                                    checked={checked}
                                    onChange={() => {
                                      setAdicionaisSelecionados((prev) => {
                                        const arr = Array.isArray(prev) ? prev : [];
                                        const ja = arr.some((a) => normalizarTexto(a?.nome) === normalizarTexto(nomeMarcado));
                                        if (ja) return arr.filter((a) => normalizarTexto(a?.nome) !== normalizarTexto(nomeMarcado));
                                        return [...arr, { nome: nomeMarcado, preco: Number(guarnicao.preco) || 0, quantidade: 1 }];
                                      });
                                    }}
                                  />
                                  <div className="adicional-content">
                                    <div>
                                      <span>{guarnicao.nome}</span>
                                    </div>
                                  </div>
                                </label>
                              );
                            })}
                        </div>
                      </div>
                    </div>
                  )}

                  <div className="modal-adicionais" style={{ marginTop: '1rem' }}>
                    <div className="adicionais-header">
                      <h3>Carnes do Dia</h3>
                    </div>
                    <div className="adicionais-content">
                      {(() => {
                        const limite = obterLimiteCarnes(modalProduto, variacaoSelecionada);
                        const carneExtraCfg = (Array.isArray(adicionaisGlobais) ? adicionaisGlobais : []).find((a) => {
                          return normalizarTexto(a?.categoria) === normalizarTexto(CATEGORIA_CARNE_EXTRA) && normalizarTexto(a?.nome) === normalizarTexto(NOME_CARNE_EXTRA);
                        });
                        const precoExtra = Number(carneExtraCfg?.preco) || 0;
                        const carnesDoDia = (Array.isArray(adicionaisGlobais) ? adicionaisGlobais : [])
                          .filter((a) => a && a.disponivel !== false && normalizarTexto(a?.categoria) === normalizarTexto(CATEGORIA_CARNES_DO_DIA))
                          .sort((a, b) => (Number(a?.ordem) || 0) - (Number(b?.ordem) || 0) || String(a?.nome || '').localeCompare(String(b?.nome || '')));

                        const totalCarnes = marmitexSemCarne ? 0 : contarCarnesSelecionadas(adicionaisSelecionados);

                        return (
                          <>
                            <div className="adicionais-grid">
                              <label className="adicional-item">
                                <input
                                  type="checkbox"
                                  checked={marmitexSemCarne}
                                  onChange={(e) => {
                                    const semCarne = e.target.checked;
                                    setMarmitexSemCarne(semCarne);
                                    if (semCarne) {
                                      setMarmitexPermitirExtra(false);
                                      setAdicionaisSelecionados((prev) => {
                                        const arr = Array.isArray(prev) ? prev : [];
                                        return arr.filter((a) => !normalizarTexto(a?.nome).startsWith(normalizarTexto(PREFIXO_CARNE)));
                                      });
                                    }
                                  }}
                                />
                                <div className="adicional-content">
                                  <div>
                                    <span>Sem carne</span>
                                  </div>
                                </div>
                              </label>
                            </div>

                            <div style={{ marginTop: '0.75rem', fontSize: '0.9rem', color: '#6B7280' }}>
                              Carnes: {totalCarnes}/{limite}
                            </div>

                            <div className="adicionais-grid" style={{ marginTop: '0.75rem' }}>
                              <label className="adicional-item">
                                <input
                                  type="checkbox"
                                  checked={marmitexPermitirExtra}
                                  disabled={marmitexSemCarne || !carneExtraCfg || carneExtraCfg.disponivel === false}
                                  onChange={(e) => setMarmitexPermitirExtra(e.target.checked)}
                                />
                                <div className="adicional-content">
                                  <div>
                                    <span>
                                      Adicionar carne extra{carneExtraCfg ? ` (+ R$ ${precoExtra.toFixed(2)} cada)` : ''}
                                    </span>
                                  </div>
                                </div>
                              </label>
                            </div>

                            {carnesDoDia.length > 0 && (
                              <div className="adicionais-grid" style={{ marginTop: '0.75rem' }}>
                                {carnesDoDia.map((carne) => {
                                  const nomeMarcado = `${PREFIXO_CARNE}${carne.nome}`;
                                  const selecionada = adicionaisSelecionados.find((a) => normalizarTexto(a?.nome) === normalizarTexto(nomeMarcado));
                                  const checked = Boolean(selecionada);
                                  const qtdAtual = Math.min(20, Math.max(1, Number(selecionada?.quantidade || 1)));

                                  return (
                                    <label key={carne._id} className="adicional-item">
                                      <input
                                        type="checkbox"
                                        checked={checked}
                                        disabled={marmitexSemCarne}
                                        onChange={(e) => {
                                          if (e.target.checked) {
                                            const novoTotal = totalCarnes + 1;
                                            if (novoTotal > limite && !marmitexPermitirExtra) {
                                              toast.error(`Limite de carnes: ${limite}. Marque a opção de carne extra para adicionar mais.`);
                                              return;
                                            }
                                            if (novoTotal > limite && (!carneExtraCfg || carneExtraCfg.disponivel === false)) {
                                              toast.error('Carne extra não configurada/indisponível. Ative e defina o preço antes de adicionar carnes acima do limite.');
                                              return;
                                            }
                                            setAdicionaisSelecionados((prev) => [...(Array.isArray(prev) ? prev : []), { nome: nomeMarcado, preco: 0, quantidade: 1 }]);
                                          } else {
                                            setAdicionaisSelecionados((prev) => (Array.isArray(prev) ? prev : []).filter((a) => normalizarTexto(a?.nome) !== normalizarTexto(nomeMarcado)));
                                          }
                                        }}
                                      />
                                      <div className="adicional-content">
                                        <div>
                                          <span>{carne.nome}</span>
                                        </div>
                                      </div>
                                      {checked && !marmitexSemCarne && (
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginTop: '0.5rem' }}>
                                          <span style={{ fontSize: '0.8rem', color: '#6B7280' }}>Qtd:</span>
                                          <input
                                            type="number"
                                            min={1}
                                            max={20}
                                            value={qtdAtual}
                                            onClick={(e) => e.stopPropagation()}
                                            onChange={(e) => {
                                              const qtdNova = Math.min(20, Math.max(1, Number(e.target.value || 1)));
                                              const totalSemEste = totalCarnes - qtdAtual;
                                              const novoTotal = totalSemEste + qtdNova;
                                              if (novoTotal > limite && !marmitexPermitirExtra) {
                                                toast.error(`Limite de carnes: ${limite}. Marque a opção de carne extra para aumentar a quantidade.`);
                                                return;
                                              }
                                              if (novoTotal > limite && (!carneExtraCfg || carneExtraCfg.disponivel === false)) {
                                                toast.error('Carne extra não configurada/indisponível. Ative e defina o preço antes de adicionar carnes acima do limite.');
                                                return;
                                              }
                                              setAdicionaisSelecionados((prev) => {
                                                const arr = Array.isArray(prev) ? prev : [];
                                                return arr.map((a) => (normalizarTexto(a?.nome) === normalizarTexto(nomeMarcado) ? { ...a, quantidade: qtdNova } : a));
                                              });
                                            }}
                                            style={{ width: '70px', padding: '0.25rem 0.5rem' }}
                                          />
                                        </div>
                                      )}
                                    </label>
                                  );
                                })}
                              </div>
                            )}
                          </>
                        );
                      })()}
                    </div>
                  </div>
                </>
              )}

              {/* Adicionais Globais - Accordion */}
              {!isProdutoMarmitex(modalProduto) && adicionaisGlobais && adicionaisGlobais.length > 0 && (
                <div className="modal-adicionais">
                  <div 
                    className="adicionais-header" 
                    onClick={() => setAdicionaisAberto(!adicionaisAberto)}
                  >
                    <h3>Adicionais</h3>
                    <span className={`adicionais-arrow ${adicionaisAberto ? 'aberto' : ''}`}>
                      ▼
                    </span>
                  </div>
                  
                  {adicionaisAberto && (
                    <div className="adicionais-content">
                      {(() => {
                        const isMarmitex = isProdutoMarmitex(modalProduto);
                        const adicionaisFiltrados = isMarmitex
                          ? adicionaisGlobais.filter((a) => {
                              const cat = normalizarTexto(a?.categoria);
                              return cat !== normalizarTexto(CATEGORIA_CARNES_DO_DIA) && cat !== normalizarTexto(CATEGORIA_CARNE_EXTRA);
                            })
                          : adicionaisGlobais;

                        if (!adicionaisFiltrados || adicionaisFiltrados.length === 0) return null;

                        // Agrupar adicionais por categoria
                        const adicionaisPorCategoria = adicionaisFiltrados.reduce((acc, adicional) => {
                          const categoria = adicional.categoria || 'Geral';
                          if (!acc[categoria]) {
                            acc[categoria] = [];
                          }
                          acc[categoria].push(adicional);
                          return acc;
                        }, {});

                        return Object.entries(adicionaisPorCategoria).map(([categoria, adicionais]) => (
                          <div key={categoria} style={{ marginBottom: '1.5rem' }}>
                            {Object.keys(adicionaisPorCategoria).length > 1 && (
                              <h4 style={{ fontSize: '0.95rem', fontWeight: '600', color: '#6B7280', marginBottom: '0.75rem' }}>
                                {categoria}
                              </h4>
                            )}
                            <div className="adicionais-grid">
                              {adicionais.map(adicional => (
                                <label key={adicional._id} className="adicional-item">
                                  <input
                                    type="checkbox"
                                    checked={adicionaisSelecionados.some(a => getAdicionalKey(a) === getAdicionalKey(adicional))}
                                    onChange={() => toggleAdicional(adicional)}
                                  />
                                  <div className="adicional-content">
                                    <div>
                                      <span>{adicional.nome}</span>
                                      {adicional.descricao && (
                                        <small style={{ display: 'block', fontSize: '0.75rem', color: '#6B7280', marginTop: '0.25rem' }}>
                                          {adicional.descricao}
                                        </small>
                                      )}
                                    </div>
                                    {adicional.preco > 0 && (
                                      <span className="adicional-preco">+ R$ {adicional.preco.toFixed(2)}</span>
                                    )}
                                  </div>
                                  {adicionaisSelecionados.some(a => getAdicionalKey(a) === getAdicionalKey(adicional)) && (
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginTop: '0.5rem' }}>
                                      <span style={{ fontSize: '0.8rem', color: '#6B7280' }}>Qtd:</span>
                                      <input
                                        type="number"
                                        min={1}
                                        max={20}
                                        value={adicionaisSelecionados.find(a => getAdicionalKey(a) === getAdicionalKey(adicional))?.quantidade || 1}
                                        onChange={(e) => atualizarQuantidadeAdicional(getAdicionalKey(adicional), e.target.value)}
                                        style={{ width: '70px', padding: '0.25rem 0.5rem' }}
                                        onClick={(e) => e.stopPropagation()}
                                      />
                                    </div>
                                  )}
                                </label>
                              ))}
                            </div>
                          </div>
                        ));
                      })()}
                    </div>
                  )}
                </div>
              )}

              {/* Observações */}
              <div className="modal-observacoes">
                <label>Observações</label>
                <textarea
                  value={observacoes}
                  onChange={(e) => setObservacoes(e.target.value)}
                  placeholder="Ex: Sem cebola, bem passado..."
                  rows={3}
                />
              </div>

              {/* Quantidade */}
              <div className="modal-quantidade">
                <label>
                  Quantidade
                  {modalProduto.estoque?.quantidade !== undefined && (
                    <span className="estoque-info">
                      (Estoque: {modalProduto.estoque.quantidade} {modalProduto.estoque.unidade || 'un'})
                    </span>
                  )}
                </label>
                <div className="quantidade-controls">
                  <button 
                    onClick={() => setQuantidade(Math.max(1, quantidade - 1))}
                    disabled={quantidade <= 1}
                  >
                    <Minus size={20} />
                  </button>
                  <span>{quantidade}</span>
                  <button 
                    onClick={() => {
                      const estoqueDisponivel = modalProduto.estoque?.quantidade || 999999;
                      const quantidadeNoCarrinho = carrinho
                        .filter(item => item._id === modalProduto._id)
                        .reduce((sum, item) => sum + (item.quantidade || 0), 0);
                      const maxDisponivel = estoqueDisponivel - quantidadeNoCarrinho;
                      
                      if (quantidade < maxDisponivel) {
                        setQuantidade(quantidade + 1);
                      } else {
                        toast.error(`❌ Estoque insuficiente! Disponível: ${maxDisponivel} unidade(s)`);
                      }
                    }}
                    disabled={
                      modalProduto.estoque?.quantidade !== undefined && 
                      quantidade >= (modalProduto.estoque.quantidade - (carrinho
                        .filter(item => item._id === modalProduto._id)
                        .reduce((sum, item) => sum + (item.quantidade || 0), 0)))
                    }
                  >
                    <Plus size={20} />
                  </button>
                </div>
              </div>
            </div>
            </div>
            
            {/* Botão Adicionar - FLUTUANTE */}
            <div className="modal-footer-flutuante">
              <button 
                className="btn-adicionar-carrinho" 
                onClick={adicionarAoCarrinho}
                disabled={modalProduto.temVariacoes && !variacaoSelecionada}
              >
                <ShoppingCart size={20} />
                Adicionar ao Carrinho - R$ {calcularPrecoTotal().toFixed(2)}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Carrinho Flutuante */}
      {carrinho.length > 0 && (
        <>
          <div className="carrinho-flutuante">
            <div className="carrinho-header carrinho-header-desktop" onClick={(e) => {
              e.stopPropagation();
              document.querySelector('.carrinho-detalhes')?.classList.toggle('aberto');
            }}>
              <div>
                <ShoppingCart size={24} />
                <span>{carrinho.length} item(s) no carrinho</span>
              </div>
              <div className="carrinho-total">
                R$ {totalCarrinho.toFixed(2)}
              </div>
            </div>

            <button
              className="btn-ver-pedido-mobile"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                setCarrinhoModalOpen(true);
              }}
            >
              Ver meu pedido ({carrinho.length}) - R$ {totalCarrinho.toFixed(2)}
            </button>

            <div className="carrinho-detalhes carrinho-desktop">
              {carrinho.map((item, index) => (
                <div key={index} className="carrinho-item">
                  <div className="item-info">
                    <h4>{item.nome}</h4>
                    {item.adicionais && item.adicionais.length > 0 && (
                      <p className="item-adicionais">
                        {item.adicionais.map(a => `${Math.min(20, Math.max(1, Number(a?.quantidade || 1)))}x ${a.nome}`).join(', ')}
                      </p>
                    )}
                    <div className="item-preco">
                      R$ {((item.preco + (item.adicionais?.reduce((sum, a) => {
                        const qtd = Math.min(20, Math.max(1, Number(a?.quantidade || 1)));
                        return sum + ((a.preco || 0) * qtd);
                      }, 0) || 0)) * item.quantidade).toFixed(2)}
                    </div>
                  </div>
                  <div className="item-controls">
                    <div className="quantidade-controls-small">
                      <button onClick={() => atualizarQuantidade(index, item.quantidade - 1)}>
                        <Minus size={16} />
                      </button>
                      <span>{item.quantidade}</span>
                      <button onClick={() => atualizarQuantidade(index, item.quantidade + 1)}>
                        <Plus size={16} />
                      </button>
                    </div>
                    <button className="btn-remover" onClick={() => removerDoCarrinho(index)}>
                      <X size={16} />
                    </button>
                  </div>
                </div>
              ))}
              <div className="carrinho-footer">
                <div className="carrinho-subtotal">
                  <span>Subtotal</span>
                  <strong>R$ {totalCarrinho.toFixed(2)}</strong>
                </div>
                <button 
                  className="btn-finalizar"
                  onClick={handleAbrirCheckout}
                >
                  Finalizar Pedido
                </button>
              </div>
            </div>
          </div>

          {/* Modal do Carrinho - Mobile */}
          {carrinhoModalOpen && (
            <div className="modal-overlay carrinho-modal-overlay" onClick={() => setCarrinhoModalOpen(false)}>
              <div className="modal-produto carrinho-modal" onClick={(e) => e.stopPropagation()}>
                <button className="modal-close" onClick={() => setCarrinhoModalOpen(false)}>
                  <X size={24} />
                </button>
                
                <div className="modal-content-scroll carrinho-modal-scroll">
                  <div className="modal-content">
                    <h2 style={{ marginBottom: '1.5rem', fontSize: '1.5rem' }}>Meu Pedido</h2>
                    
                    {carrinho.map((item, index) => (
                      <div key={index} className="carrinho-item-modal">
                        <div className="item-info">
                          <h4>{item.nome}</h4>
                          {item.adicionais && item.adicionais.length > 0 && (
                            <p className="item-adicionais">
                              {item.adicionais.map(a => `${Math.min(20, Math.max(1, Number(a?.quantidade || 1)))}x ${a.nome}`).join(', ')}
                            </p>
                          )}
                          <div className="item-preco">
                            R$ {((item.preco + (item.adicionais?.reduce((sum, a) => {
                              const qtd = Math.min(20, Math.max(1, Number(a?.quantidade || 1)));
                              return sum + ((a.preco || 0) * qtd);
                            }, 0) || 0)) * item.quantidade).toFixed(2)}
                          </div>
                        </div>
                        <div className="item-controls">
                          <div className="quantidade-controls-small">
                            <button onClick={() => atualizarQuantidade(index, item.quantidade - 1)}>
                              <Minus size={16} />
                            </button>
                            <span>{item.quantidade}</span>
                            <button onClick={() => atualizarQuantidade(index, item.quantidade + 1)}>
                              <Plus size={16} />
                            </button>
                          </div>
                          <button className="btn-remover" onClick={() => removerDoCarrinho(index)}>
                            <X size={16} />
                          </button>
                        </div>
                      </div>
                    ))}
                    
                    <div className="carrinho-footer-modal">
                      <div className="carrinho-subtotal">
                        <span>Subtotal</span>
                        <strong>R$ {totalCarrinho.toFixed(2)}</strong>
                      </div>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem', marginTop: '1rem' }}>
                        <button 
                          className="btn-continuar-comprando"
                          onClick={() => setCarrinhoModalOpen(false)}
                        >
                          Continuar Comprando
                        </button>
                        <button 
                          className="btn-finalizar"
                          onClick={() => {
                            setCarrinhoModalOpen(false);
                            handleAbrirCheckout();
                          }}
                        >
                          Finalizar Pedido
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </>
      )}

      {/* Checkout Modal */}
      <CheckoutModal
        isOpen={checkoutOpen}
        onClose={() => setCheckoutOpen(false)}
        carrinho={carrinho}
        estabelecimento={estabelecimento}
        onPedidoCriado={(pedido) => {
          setCarrinho([]);
          setCheckoutOpen(false);
          // Opcional: redirecionar para página de pedidos
          // navigate(`/pedidos/${pedido._id}`);
        }}
      />
      
      {/* Debug: Log do estabelecimento quando checkout abrir */}
      {checkoutOpen && estabelecimento && (
        <div style={{ display: 'none' }}>
          {console.log('🔍 CARDAPIO - Estabelecimento passado para CheckoutModal:', {
            id: estabelecimento._id,
            nome: estabelecimento.nome,
            temConfig: !!estabelecimento.configuracoes,
            temTaxas: !!estabelecimento.configuracoes?.taxasPorBairro,
            qtdTaxas: estabelecimento.configuracoes?.taxasPorBairro?.length || 0,
            taxasPorBairro: estabelecimento.configuracoes?.taxasPorBairro?.slice(0, 3)
          })}
        </div>
      )}

      {/* Footer */}
      <footer className="cardapio-footer">
        <div className="footer-content">
          <div className="footer-section">
            <div className="footer-logo">
              <img 
                src="/logo-cantinho.png" 
                alt="Cantinho do Sabor"
                className="footer-logo-img"
              />
            </div>
            <p className="footer-description">
              Os melhores lanches com sabor único! 
              Entrega rápida e qualidade garantida.
            </p>
            <div className="footer-social">
              <a href="https://instagram.com" target="_blank" rel="noopener noreferrer" className="social-link">
                <Instagram size={24} />
              </a>
              <a href="https://facebook.com" target="_blank" rel="noopener noreferrer" className="social-link">
                <Facebook size={24} />
              </a>
              <a href="https://wa.me/5528999999999" target="_blank" rel="noopener noreferrer" className="social-link">
                <MessageCircle size={24} />
              </a>
            </div>
          </div>

          <div className="footer-section">
            <h3>Contato</h3>
            <div className="footer-contact">
              <a href={`tel:${estabelecimento?.contato?.telefone || '+5528999999999'}`} className="contact-item">
                <Phone size={18} />
                <span>{estabelecimento?.contato?.telefone || '(28) 99999-9999'}</span>
              </a>
              <a href={`mailto:${estabelecimento?.contato?.email || 'contato@cantinhodosabor.com'}`} className="contact-item">
                <Mail size={18} />
                <span>{estabelecimento?.contato?.email || 'contato@cantinhodosabor.com'}</span>
              </a>
              <div className="contact-item">
                <MapPin size={18} />
                <span>
                  {(estabelecimento?.endereco?.rua || 'Rua Alberto Boreli')}, {estabelecimento?.endereco?.numero || '37'} - {estabelecimento?.endereco?.bairro || 'Caiçara'}
                  <br />
                  {estabelecimento?.endereco?.cidade || 'Cachoeiro de Itapemirim'} - {estabelecimento?.endereco?.estado || 'ES'}
                </span>
              </div>
            </div>
          </div>

          <div className="footer-section">
            <h3>Horário de Funcionamento</h3>
            <div className="footer-hours">
              <div className="hours-item">
                <span>Segunda-feira</span>
                <span>17:00 - 23:40</span>
              </div>
              <div className="hours-item">
                <span>Terça a Domingo</span>
                <span>18:00 - 23:40</span>
              </div>
            </div>
          </div>

          <div className="footer-section">
            <h3>Links Rápidos</h3>
            <div className="footer-links">
              <a href="#cardapio">Cardápio</a>
              <a href="#sobre">Sobre Nós</a>
              <a href="#contato">Contato</a>
              <a href="#pedidos">Meus Pedidos</a>
            </div>
          </div>
        </div>

        <div className="footer-bottom">
          <p>&copy; {new Date().getFullYear()} {estabelecimento?.nome || 'Cantinho do Sabor'}. Todos os direitos reservados.</p>
          <p className="footer-dev">Desenvolvido com ❤️ para você</p>
        </div>
      </footer>
    </div>
  );
};

export default Cardapio;
